import { useFloating, UseFloatingOptions } from "@floating-ui/react";
import classNames from "classnames";
import { SearchAutocomplete } from "PFApp/components/search_autocomplete";
import {
  SearchConfig,
  SearchEntityData
} from "PFApp/components/search_autocomplete/search_autocomplete.types";
import { AUTOCOMPLETE_ACTION_ICON_ID } from "PFApp/components/search_input";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { useRecentSearches } from "PFCore/helpers/use_recent_searches";
import { IconName } from "PFTheme/graphics/icons";
import { DataDogProps } from "PFTypes/datadog";
import { EventKey } from "PFTypes/event_key";
import { RecentSearchesPageKey } from "PFTypes/recent_searches";
import { MouseEvent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import css from "./search_input.module.scss";
import { useSearchInput } from "./use_search_input";

const INPUT_VALUE_MAX_LENGTH = 255;

export type SearchInputProps = {
  term: string;
  onTermChange: (newTerm: string, entity?: SearchEntityData | null) => void;
  qaId?: string;
  placeholder?: string;
  disabled?: boolean;
  hideFooter?: boolean;
  autocompleteConfig?: SearchConfig;
  pageKey: RecentSearchesPageKey;
  includeRecentSearches?: boolean;
  floatingOptions?: Partial<UseFloatingOptions>;
  classes?: {
    root?: string;
    dropdown?: string;
  };
  fullWidth?: boolean;
} & DataDogProps;

export const SearchInput = ({
  term,
  onTermChange,
  qaId,
  placeholder,
  disabled,
  hideFooter,
  autocompleteConfig = [],
  pageKey,
  includeRecentSearches = true,
  floatingOptions,
  classes = {},
  ddActionName,
  fullWidth
}: SearchInputProps) => {
  const { t } = useTranslation("translation");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef(null);
  const autocompleteListId = `${qaId ?? "search"}-autocomplete-list`;

  const { getRecentSearches, saveSearchAsRecent } = useRecentSearches(pageKey);

  useEffect(() => {
    if (includeRecentSearches) {
      saveSearchAsRecent(term);
    }
  }, [term]);

  const {
    localTerm,
    isDropdownOpened,
    setIsDropdownOpened,
    setLocalTerm,
    handleClear,
    handleSubmit,
    handleInputChange,
    handleBlur,
    handleTermChange
  } = useSearchInput({
    term,
    onTermChange,
    withUrlChanges: false,
    searchInputRef
  });

  const { refs, floatingStyles } = useFloating({
    placement: "bottom-start",
    ...floatingOptions
  });

  const handleKeyDown = (event) => {
    if (event.key === EventKey.Enter) {
      handleSubmit();
    }
    // TODO: [PROF-7192] It should be refactored as a part of Dropdown refactor ticket
    // This is a hack to make the dropdown work with keyboard navigation
    // The same implementation is done in select.tsx
    // @ts-ignore - Related to PROF-7192
    dropdownRef.current && dropdownRef.current.handleKeyDown(event);
  };

  const isSearchIconDisplayed = !term || isDropdownOpened;
  const isSearchActionDisabled = disabled || (!localTerm && !term);
  const withAutocomplete = includeRecentSearches || !!autocompleteConfig.length;
  const showAutocomplete = withAutocomplete && isDropdownOpened;

  const onActionIconClick = (event: MouseEvent) => {
    event.preventDefault();
    if (isSearchIconDisplayed) {
      handleSubmit();
    } else {
      handleClear();
    }
  };

  const actions = [
    {
      id: AUTOCOMPLETE_ACTION_ICON_ID,
      name: (isSearchIconDisplayed ? "search" : "cross") as IconName,
      onClick: onActionIconClick,
      disabled: disabled || (!localTerm && !term),
      title: isSearchIconDisplayed ? t("applyTheSearchTerm") : t("clearTheSearchTerm")
    }
  ];

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className={classNames(css.search, classes.root, {
        [css.fixedWidth]: !fullWidth,
        [css.fullWidth]: !!fullWidth
      })}
      onBlur={handleBlur}
      role="search"
      aria-label={placeholder ?? t("search")}
    >
      <div ref={refs.setReference} className={css.inputWrapper}>
        <InputFieldSet
          ref={searchInputRef}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={() => setIsDropdownOpened(true)}
          onBlur={handleBlur}
          placeholder={placeholder ?? t("search")}
          value={localTerm}
          qaId={qaId}
          disabled={disabled}
          minVisibleMaxLengthFactor={2}
          maxLength={INPUT_VALUE_MAX_LENGTH}
          inputClassName={css.searchInput}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showAutocomplete}
          aria-controls={autocompleteListId}
          actions={actions}
          data-dd-action-name={ddActionName}
        />
      </div>
      {showAutocomplete && (
        <div
          id={autocompleteListId}
          // eslint-disable-next-line react-hooks/refs
          ref={refs.setFloating}
          style={{ ...floatingStyles, zIndex: 30 }}
          className={classNames({ [css.fullWidth]: !!fullWidth })}
        >
          <SearchAutocomplete
            dropdownRef={dropdownRef}
            config={autocompleteConfig}
            recentSearches={includeRecentSearches ? getRecentSearches(localTerm) : []}
            term={localTerm}
            onTermChange={(text, entity) => {
              setLocalTerm(text);
              handleTermChange(text, entity);
            }}
            onClose={() => setIsDropdownOpened(false)}
            showFooter={!hideFooter && !isSearchActionDisabled}
            classes={{ dropdown: classNames(classes.dropdown, { [css.fullWidth]: !!fullWidth }) }}
          />
        </div>
      )}
    </div>
  );
};
